import { SearchExpression } from './SearchExpression';
import { SearchVisibility } from './SearchVisibility';
import { MultiTextField } from './MultiTextField';
import { MultiTagField } from './MultiTagField';
import { MultiFullTextField } from './MultiFullTextField';
import { MultiSearchTermField } from './MultiSearchTermField';
import { DateRangeField } from './DateRangeField';
import { NumberRangeField } from './NumberRangeField';
import { MultiCollectionField } from './MultiCollectionField';
import { SingleBooleanField } from './SingleBooleanField';
import { BatesRangeField } from './BatesRangeField';
import { MultiNamedEntityField } from './MultiNamedEntityField';
import { BaseSearch } from '../BaseSearch';
import { SearchContext } from '../ISearch';

export class AdvancedSearch extends BaseSearch<SearchExpression> {
    public readonly fields;

    constructor(searchContext: SearchContext, expression: SearchExpression = new SearchExpression()) {
      super(searchContext, expression);

      this.visibility = SearchVisibility.Public;

      this.fields = [
        new MultiTextField("resourceId", "Folio Identifier"),
        new MultiTagField("tags", "Tags"),
        new MultiNamedEntityField("namedEntities", "Named Entities"),
        new MultiSearchTermField("searchTerm", "Search Term"),
        new MultiTextField("custodian", "Custodian"),
        new MultiFullTextField("fullText", "Full Text"),
        new MultiFullTextField("translatedText", "Translated Text"),
        new DateRangeField("createdDate", "Created Date"),
        new MultiTextField("fileExtension", "File Extension"),
        new MultiTextField("fileName", "File Name"),
        new MultiFullTextField("metadataText", "Metadata Text"),
        new MultiFullTextField("title", "Title"),
        new MultiTextField("creator", "Creator"),
        new MultiTextField("emailTo", "To"),
        new MultiTextField("emailFrom", "From"),
        new MultiTextField("emailCc", "Cc"),
        new MultiTextField("emailBcc", "Bcc"),
        new MultiTextField("emailRecipients", "Recipients"),
        new MultiTextField("emailMessageClass", "Message Class"),
        new MultiTextField("emailDomains", "Domains"),
        new MultiTextField("emailConversationTopic", "Conversation Topic"),
        new MultiTextField("folders", "Folder"),
        new MultiTextField("path", "Path"),
        new MultiTextField("category", "File Category"),
        new MultiTextField("type", "Record Type"),
        new MultiTextField("mimeType", "Mime Type"),
        new MultiTextField("md5", "MD5"),
        new NumberRangeField("fileSize", "Size (MB)", 1000000),
        new NumberRangeField("pageCount", "Page Count", 1),
        new DateRangeField("modifiedDate", "Modified Date"),
        new DateRangeField("accessedDate", "Accessed Date"),
        new DateRangeField("printDate", "Print Date"),
        new MultiFullTextField("userMetadata", "User Metadata"),
        new MultiFullTextField("notes", "Notes"),
        new SingleBooleanField("hasNotes", "Has Notes", true),
        new SingleBooleanField("hasPrivilegedNotes", "Has Privileged Notes", true),
        new SingleBooleanField("isSourceStorage", "Is Source Data", true),
        new SingleBooleanField("isTopParentResource", "Is Top Parent Resource", true),
        new SingleBooleanField("isContainer", "Is Container", true),
        new BatesRangeField("resourceBates.begBates", "Bates")
      ];

      if (searchContext == SearchContext.Project) {
        this.fields = [new MultiCollectionField("collection", "Collection"), ...this.fields];
      }
    }

    public static From(search): AdvancedSearch {
      // Guard against context being null/undefined in server-stored searches;
      // default to Project so the Collection field is included.
      const context = search.context != null ? search.context : SearchContext.Project;

      let advancedSearch = $.extend(true, new AdvancedSearch(context), search);

      advancedSearch.expression = SearchExpression.From(search.expression);

      return advancedSearch;
    }

    public static FromList(searches: Array<any>): Array<AdvancedSearch> {
      const advancedSearches = [];

      searches.forEach(s => advancedSearches.push(AdvancedSearch.From(s)));

      return advancedSearches;
    }
}
// Expose on legacy global namespace so plain-JS files (e.g. navigation.js) can access app.model.search.AdvancedSearch
(window as any).app = (window as any).app || {};
(window as any).app.model = (window as any).app.model || {};
(window as any).app.model.search = (window as any).app.model.search || {};
(window as any).app.model.search.AdvancedSearch = AdvancedSearch;
